import React from 'react';

export default function DirectorDashboard() {
  const operationalMetrics = [
    { title: 'Active Orders', value: '156', change: '+12%', icon: 'ri-shopping-bag-line' },
    { title: 'Pending Approvals', value: '23', change: '-8%', icon: 'ri-time-line' },
    { title: 'Quality Issues', value: '3', change: '-50%', icon: 'ri-error-warning-line' },
    { title: 'Vendor Performance', value: '94%', change: '+3%', icon: 'ri-user-star-line' },
  ];

  const departmentStatus = [
    { dept: 'Sales', status: 'Excellent', performance: 95, color: 'green' },
    { dept: 'Operations', status: 'Good', performance: 88, color: 'blue' },
    { dept: 'Quality Control', status: 'Good', performance: 92, color: 'blue' },
    { dept: 'Finance', status: 'Excellent', performance: 97, color: 'green' },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-primary">Director Dashboard</h1>
        <div className="flex gap-3">
          <button className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-file-chart-line mr-2"></i>
            Strategic Report
          </button>
          <button className="border border-primary text-primary px-4 py-2 rounded-lg hover:bg-primary/5 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-team-line mr-2"></i>
            Team Review
          </button>
        </div>
      </div>

      {/* Operational Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {operationalMetrics.map((metric, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{metric.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{metric.value}</p>
                <p className="text-sm text-green-600 mt-1">{metric.change}</p>
              </div>
              <div className="p-3 bg-primary/10 rounded-lg">
                <i className={`${metric.icon} text-primary text-xl`}></i>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Department Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h2 className="text-xl font-semibold text-primary mb-4">Department Performance</h2>
          <div className="space-y-4">
            {departmentStatus.map((dept, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-semibold text-gray-900">{dept.dept}</h3>
                  <p className={`text-sm text-${dept.color}-600`}>{dept.status}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-20 bg-gray-200 rounded-full h-2">
                    <div 
                      className={`bg-${dept.color}-500 h-2 rounded-full`}
                      style={{ width: `${dept.performance}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">{dept.performance}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h2 className="text-xl font-semibold text-primary mb-4">Strategic Initiatives</h2>
          <div className="space-y-4">
            <div className="p-4 border-l-4 border-blue-500 bg-blue-50">
              <h3 className="font-semibold text-blue-900">Digital Transformation</h3>
              <p className="text-sm text-blue-700 mt-1">Implementation of new POS system across all branches</p>
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-blue-600">Progress: 75%</span>
                <span className="text-xs text-blue-600">Due: Feb 2024</span>
              </div>
            </div>
            <div className="p-4 border-l-4 border-green-500 bg-green-50">
              <h3 className="font-semibold text-green-900">Quality Enhancement</h3>
              <p className="text-sm text-green-700 mt-1">New quality control processes and training</p>
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-green-600">Progress: 90%</span>
                <span className="text-xs text-green-600">Due: Jan 2024</span>
              </div>
            </div>
            <div className="p-4 border-l-4 border-yellow-500 bg-yellow-50">
              <h3 className="font-semibold text-yellow-900">Vendor Expansion</h3>
              <p className="text-sm text-yellow-700 mt-1">Onboarding 5 new premium vendors</p>
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-yellow-600">Progress: 40%</span>
                <span className="text-xs text-yellow-600">Due: Mar 2024</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* High Value Approvals */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-xl font-semibold text-primary mb-4">High Value Approvals Required</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Request Type</th>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Details</th>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Value</th>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Requested By</th>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Priority</th>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t">
                <td className="px-4 py-3">
                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">Discount</span>
                </td>
                <td className="px-4 py-3">VIP customer - 30% discount on wedding set</td>
                <td className="px-4 py-3 font-medium">₹5,50,000</td>
                <td className="px-4 py-3">HO Manager</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">High</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button className="text-green-600 hover:text-green-800 cursor-pointer">
                      <i className="ri-check-line"></i>
                    </button>
                    <button className="text-red-600 hover:text-red-800 cursor-pointer">
                      <i className="ri-close-line"></i>
                    </button>
                    <button className="text-blue-600 hover:text-blue-800 cursor-pointer">
                      <i className="ri-eye-line"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr className="border-t">
                <td className="px-4 py-3">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">Vendor</span>
                </td>
                <td className="px-4 py-3">New vendor onboarding - Premium Diamonds Ltd</td>
                <td className="px-4 py-3 font-medium">₹10,00,000</td>
                <td className="px-4 py-3">Operations Head</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">Medium</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button className="text-green-600 hover:text-green-800 cursor-pointer">
                      <i className="ri-check-line"></i>
                    </button>
                    <button className="text-red-600 hover:text-red-800 cursor-pointer">
                      <i className="ri-close-line"></i>
                    </button>
                    <button className="text-blue-600 hover:text-blue-800 cursor-pointer">
                      <i className="ri-eye-line"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}